import { inject, Injectable } from '@angular/core';
import { Assignment, isAssignmentFeedingRecommendationDetailsModel, Plan } from '@pxl/models/common';
import { FormatService } from '@pxl/models/contracts';
import { MedicationModel, VeterinarianModel, WeightModel } from '@pxl/shared/generated';
import { LanguageService, RESOURCES } from '@pxl/shared/translation';
import { dateFormats, DateTimeHelper } from '@pxl/shared/util2';

@Injectable()
export class DefaultFormatService implements FormatService {
    private readonly languageService = inject(LanguageService);

    public getFormattedWeightValueWithUnit(weightModel: WeightModel): string {
        const formattedWeight = this.getFormattedWeightValue(weightModel);

        if (weightModel.userUsesPoundsInsteadOfKilograms) {
            const translatedUnitPound = this.languageService.translate('petsxl.ui.global.units.pounds-short');
            return `${formattedWeight} ${translatedUnitPound}`;
        }

        const translatedUnitKg = this.languageService.translate('petsxl.ui.global.units.kilogram-short');
        return `${formattedWeight} ${translatedUnitKg}`;
    }

    public formatWeightKg(valueInKilograms: number): string {
        return this.formatNumber(valueInKilograms, '1.3-3');
    }

    public formatWeightPound(valueInPounds: number) {
        return this.formatNumber(valueInPounds, '1.2-2');
    }

    public containsNumberGroupSeparator(str: string): boolean {
        if (!str) {
            return false;
        }

        const groupSeparator: string = this.getNumberGroupSeparator();
        return str.includes(groupSeparator);
    }

    public getNumberGroupSeparator(): string {
        return this.getNumberSeparator('group');
    }

    public containsNumberInvalidCharacters(str: string): boolean {
        if (!str) {
            return false;
        }

        const decimalSeparator = this.getNumberSeparator('decimal');
        const pattern = '^[+-]?([0-9]+\\' + decimalSeparator + '?[0-9]*)$';
        const validRegEx = new RegExp(pattern);

        return !validRegEx.test(str);
    }

    public parseStringNumber(str: string): number | null {
        if (!str) {
            return null;
        }

        const decimalSeparator = this.getNumberSeparator('decimal');
        const groupSeparator = this.getNumberSeparator('group');
        const decimalSeparatorRegex = new RegExp('\\' + decimalSeparator, 'g');
        const groupSeparatorRegex = new RegExp('\\' + groupSeparator, 'g');
        const normalizedString = str.replace(groupSeparatorRegex, '').replace(decimalSeparatorRegex, '.');

        const result = Number(normalizedString);

        if (Number.isNaN(result) || !Number.isFinite(result)) {
            return null;
        }

        return result;
    }

    /**
     * DigitsInfo:
     * {minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}
     * 1.2-2 => 1,00
     *
     * withNumberGroupSeparator:
     *  e.g. 2000 => 2.000
     */
    public formatNumber(num: number, digitsInfo: string, withNumberGroupSeparator = true): string {
        const locale = this.languageService.getCulture();
        const [minInt, fracPart] = digitsInfo.split('.');
        const [minFrac, maxFrac] = (fracPart ?? '0-0').split('-').map(Number);
        const formatter = new Intl.NumberFormat(locale, {
            minimumIntegerDigits: Number(minInt) || 1,
            minimumFractionDigits: minFrac,
            maximumFractionDigits: maxFrac,
            useGrouping: withNumberGroupSeparator,
        });
        return formatter.format(num);
    }

    private getNumberSeparator(type: 'group' | 'decimal'): string {
        const locale = this.languageService.getCulture();
        const testValue = type === 'group' ? 1000 : 1.1;
        const parts = new Intl.NumberFormat(locale).formatToParts(testValue);
        const defaults = { group: ',', decimal: '.' };
        return parts.find((p) => p.type === type)?.value ?? defaults[type];
    }

    public getFormattedShortDate(date: Date | string): string {
        return DateTimeHelper.new(date).toLocalizedDateFormat();
    }
    public getFormattedDateTime(date: Date | string): string {
        return DateTimeHelper.new(date).toLocalizedDateTimeFormat();
    }

    public getFormattedShortDateAndTime(date: Date | string): string {
        return DateTimeHelper.new(date).toLocalizedDateTimeFormat();
    }

    public formatAssignmentDate(assignment: Assignment): string {
        const assignmentDate = DateTimeHelper.new(assignment.performedDateUtc || assignment.getAssignmentDateUtc());

        if (assignment.isTreatment() || assignment.isVaccination())
            return assignmentDate.toFormat(dateFormats.momentLocalizedDateFormat, assignment.veterinarian?.lastKnownTimeZoneIana);

        return assignmentDate.toFormat(dateFormats.momentLocalizedDateTimeAltFormat);
    }

    public formatAssignmentPerformedDate(assignment: Assignment): string {
        if (!assignment.isPerformed()) return '';
        if (assignment.isVaccination()) {
            return DateTimeHelper.new(assignment.getPerformedOrAssignmentDateUtc()).toFormat(dateFormats.momentLocalizedDateFormat);
        }
        return DateTimeHelper.new(assignment.getPerformedOrAssignmentDateUtc()).toFormat(dateFormats.momentLocalizedDateTimeAltFormat);
    }

    public formatMedicationPackageContent(medication: MedicationModel) {
        if (medication.amount === null) {
            return medication.unit;
        }

        const formattedAmount = this.formatNumber(medication.amount, '1.0-3', false);

        if (medication.unit !== null) {
            return `${formattedAmount} ${medication.unit}`;
        }

        return `${formattedAmount}`;
    }

    public formatMedicationSubtitle(medicationTypeResource: string, medicationTreatmentDate: string): string {
        const medicationTypeText = this.languageService.translate(medicationTypeResource);
        const treatmentDateString = DateTimeHelper.new(medicationTreatmentDate).toDateFormat();

        return [medicationTypeText, '|', treatmentDateString].join(' ');
    }

    public formatMedicationInstructionsStartAndDuration(medication: MedicationModel, veterinarian: VeterinarianModel): string | null {
        const durationOfAdministration = medication.usageInstructions?.durationOfAdministration;
        const startUtc = medication.usageInstructions?.startUtc;

        const textMedicationFrom = this.languageService.translate(RESOURCES.petsxl.ui.medication.medication_detail.text.duration_from);

        if (startUtc) {
            const formattedStartUtc = DateTimeHelper.new(startUtc).toFormat(
                dateFormats.momentLocalizedDateFormat,
                veterinarian?.lastKnownTimeZoneIana ?? '',
            );

            if (durationOfAdministration) {
                return `${durationOfAdministration} ${textMedicationFrom} ${formattedStartUtc}`;
            } else {
                return `${textMedicationFrom} ${formattedStartUtc}`;
            }
        } else if (durationOfAdministration) {
            return durationOfAdministration;
        }

        return null;
    }

    public formatMedicationInstructionsDosage(medication: MedicationModel): string | null | undefined {
        const frequencyOfAdministration = medication.usageInstructions?.frequencyOfAdministration;
        const routeOfAdministration = medication.usageInstructions?.routeOfAdministration;

        if (frequencyOfAdministration && routeOfAdministration) {
            return `${frequencyOfAdministration}, ${routeOfAdministration}`;
        } else if (frequencyOfAdministration) {
            return frequencyOfAdministration;
        } else {
            return routeOfAdministration;
        }
    }

    public formatMedicationApplicationNoteByMedicationPlan(medicationPlan: Plan): string {
        const medicationDetails = medicationPlan.getPlanMedicationDetailsModel();
        if (!medicationDetails) return '';
        const { amount } = medicationDetails;
        const { medicationUnit, medicationRouteOfAdministration, medication } = medicationPlan;
        if (!medicationUnit || !medicationRouteOfAdministration) return '';
        return this.formatMedicationApplicationNote(
            amount,
            medicationUnit.resourceKey,
            medicationRouteOfAdministration.resourceKey,
            medication?.text,
        );
    }

    public formatMedicationApplicationNoteByMedicationAssignment(medicationAssignment: Assignment, showMedicationText = true): string {
        const { medicationUnit, medicationRouteOfAdministration, medication } = medicationAssignment;
        const { amount } = medicationAssignment.plan.getPlanMedicationDetailsModel();
        if (!medicationUnit || !medicationRouteOfAdministration) return '';
        return this.formatMedicationApplicationNote(
            amount,
            medicationUnit.resourceKey,
            medicationRouteOfAdministration.resourceKey,
            showMedicationText ? medication?.text : null,
        );
    }

    public formatAnimalFeedingApplicationNote(assignment: Assignment): string {
        const feedingRecommendationDetails = assignment.additionalDetails.find((aD) => isAssignmentFeedingRecommendationDetailsModel(aD));
        if (!feedingRecommendationDetails) return '';
        return `${assignment.name}, ${feedingRecommendationDetails.customUserData.dailyRation} ${feedingRecommendationDetails.customUserData.dailyRationUnit}`;
    }

    public formatAnimalFeedingRation(assignment: Assignment): string {
        const feedingRecommendationDetails = assignment.additionalDetails.find((aD) => isAssignmentFeedingRecommendationDetailsModel(aD));
        if (!feedingRecommendationDetails) return '';
        return `${feedingRecommendationDetails.customUserData.dailyRation} ${feedingRecommendationDetails.customUserData.dailyRationUnit}`;
    }

    private getFormattedWeightValue(weightModel: WeightModel): string {
        if (weightModel.userUsesPoundsInsteadOfKilograms) {
            return this.formatWeightPound(weightModel.value);
        }

        return this.formatWeightKg(weightModel.value);
    }

    private formatMedicationApplicationNote(
        amount: number,
        medicationUnitResource: string,
        routeOfAdministrationResource: string,
        medicationText?: string,
        dateTime?: string | Date,
    ): string {
        const formattedDateTime = !dateTime ? '' : ` - ${DateTimeHelper.new(dateTime).toDateFormat()}`;
        const formattedMedicationText = medicationText == null ? '' : `${medicationText}, `;
        const formattedAmount = this.formatNumber(amount, '1.0-3');
        const medicationUnit = this.languageService.translate(medicationUnitResource);
        const routeOfAdministration = this.languageService.translate(routeOfAdministrationResource);
        return `${formattedMedicationText}${formattedAmount} ${medicationUnit}, ${routeOfAdministration}${formattedDateTime}`;
    }
}
